import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveContainer } from 'recharts';

/**
 * SafeResponsiveContainer - A wrapper that prevents Recharts from crashing
 * when the container has invalid dimensions (e.g., during back navigation).
 * 
 * The problem: ResponsiveContainer tries to render before the DOM element
 * has proper dimensions, causing crashes with -1 width/height values.
 * 
 * The fix: Use ResizeObserver to wait for valid dimensions before rendering.
 */
export const SafeResponsiveContainer = ({ 
  children, 
  width = "100%", 
  height = "100%",
  minWidth = 50,
  minHeight = 50,
  fallback = null,
  ...props 
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Check initial dimensions
    const checkDimensions = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width >= minWidth && rect.height >= minHeight) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // If dimensions are already valid, render immediately
    if (checkDimensions()) return;

    // Otherwise, use ResizeObserver to wait for valid dimensions
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width >= minWidth && height >= minHeight) {
          setIsReady(true);
          observer.disconnect();
          break;
        }
      }
    });

    observer.observe(container);

    // Fallback timeout - if dimensions aren't ready after 2s, try anyway
    const timeout = setTimeout(() => {
      if (!isReady) {
        setIsReady(true);
      }
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [minWidth, minHeight, isReady]);

  // Reset ready state if component unmounts and remounts
  useEffect(() => {
    return () => setIsReady(false);
  }, []);

  return (
    <div 
      ref={containerRef} 
      style={{ width, height, minWidth, minHeight }}
    >
      {isReady ? (
        <ResponsiveContainer width="100%" height="100%" {...props}>
          {children}
        </ResponsiveContainer>
      ) : (
        fallback || (
          <div className="flex items-center justify-center w-full h-full bg-slate-50 rounded animate-pulse">
            <span className="text-sm text-slate-400">Loading chart...</span>
          </div>
        )
      )}
    </div>
  );
};

/**
 * SafePDFViewer - A wrapper that prevents PDFViewer from crashing
 * when rendered before the blob URL is ready or during navigation transitions.
 */
export const SafePDFViewer = ({ 
  children, 
  width = "100%", 
  height = "100%",
  showToolbar = false,
  fallback = null,
  PDFViewerComponent,
  ...props 
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Small delay to ensure layout is complete
    const timeout = setTimeout(() => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setIsReady(true);
      }
    }, 100);

    // ResizeObserver as backup
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setIsReady(true);
          break;
        }
      }
    });

    observer.observe(container);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, []);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-50 rounded-lg p-8">
        <span className="text-slate-500">PDF preview unavailable. Use the Export button to download.</span>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      style={{ width, height }}
      className="relative"
    >
      {isReady && PDFViewerComponent ? (
        <PDFViewerComponent width="100%" height="100%" showToolbar={showToolbar} {...props}>
          {children}
        </PDFViewerComponent>
      ) : (
        fallback || (
          <div className="flex items-center justify-center w-full h-full bg-slate-50 rounded animate-pulse">
            <span className="text-sm text-slate-400">Loading preview...</span>
          </div>
        )
      )}
    </div>
  );
};

export default SafeResponsiveContainer;
